import { BinLoader, gunzip } from './BinLoader';
import { ByteStream } from './ByteStream';
import { IniData, parseIni } from './ini';
import { AREA_HEIGHT, AREA_WIDTH, INT_MIN, KnyttArea } from './KnyttArea';
import { KnyttPoint } from './KnyttPoint';
import { KnyttWorldInfo } from './KnyttWorldInfo';

const cp1252 = new TextDecoder('windows-1252');

/**
 * A loaded Knytt world. Holds parsed World.ini, all areas, and resolves
 * data files from the .knytt.bin archive (falling back to bundled system data
 * resolved via getSystemUrl()).
 */
export class KnyttWorld {
  iniData: IniData = new IniData();
  info!: KnyttWorldInfo;
  binLoader: BinLoader;

  areas = new Map<string, KnyttArea>(); // KnyttPoint.key -> area
  minBounds = new KnyttPoint(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
  maxBounds = new KnyttPoint(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
  size = KnyttPoint.Zero;

  /** Display/identity name (the bin root directory, e.g. "Nifflas - Tutorial"). */
  worldName: string;

  constructor(binLoader: BinLoader) {
    this.binLoader = binLoader;
    this.worldName = binLoader.rootDirectory;
  }

  static async load(binBytes: Uint8Array): Promise<KnyttWorld> {
    const loader = new BinLoader(binBytes);
    const world = new KnyttWorld(loader);

    const iniBytes = loader.getFile('World.ini');
    if (!iniBytes) throw new Error('World.ini not found in archive');
    world.loadWorldConfig(cp1252.decode(iniBytes));

    const mapBytes = loader.getFile('Map.bin');
    if (!mapBytes) throw new Error('Map.bin not found in archive');
    await world.loadWorldMap(mapBytes);

    return world;
  }

  loadWorldConfig(iniText: string): void {
    this.iniData = parseIni(iniText);
    this.info = new KnyttWorldInfo(this.iniData.getSection('World'));
  }

  async loadWorldMap(gzipped: Uint8Array): Promise<void> {
    const raw = await gunzip(gzipped);
    const stream = new ByteStream(raw);

    // Skip optional empty header block.
    let startByte = stream.readByte();
    if (startByte === 0) {
      const offset =
        stream.readByte() +
        (stream.readByte() << 8) +
        (stream.readByte() << 16) +
        (stream.readByte() << 24);
      stream.skip(offset >>> 0);
      startByte = stream.readByte();
    }
    // Scan to first 'x'.
    while (startByte !== 0x78 /* 'x' */ && startByte !== -1) startByte = stream.readByte();

    this.areas = new Map();
    while (startByte === 0x78) {
      const area = new KnyttArea(this, stream);
      if (area.position.x === INT_MIN || area.position.y === INT_MIN) {
        startByte = stream.readByte();
        continue;
      }
      this.areas.set(area.position.key, area);
      this.minBounds = this.minBounds.min(area.position);
      this.maxBounds = this.maxBounds.max(area.position);
      startByte = stream.readByte();
    }

    this.size = new KnyttPoint(
      this.maxBounds.x - this.minBounds.x + 1,
      this.maxBounds.y - this.minBounds.y + 1,
    );
  }

  /** Returns the area at coords, an empty area if the INI defines it, else null. */
  getArea(coords: KnyttPoint): KnyttArea | null {
    const existing = this.areas.get(coords.key);
    if (existing) return existing;
    if (this.iniData.hasSection(`x${coords.x}y${coords.y}`)) {
      return new KnyttArea(this, coords);
    }
    return null;
  }

  getMapIndex(coords: KnyttPoint): number {
    return (coords.y - this.minBounds.y) * this.size.x + (coords.x - this.minBounds.x);
  }

  getMapLength(): number {
    return this.size.x * this.size.y;
  }

  /** Raw bytes for a world-relative file, or null if it must come from system data. */
  getFileBytes(path: string): Uint8Array | null {
    return this.binLoader.getFile(path);
  }

  worldFileExists(path: string): boolean {
    return this.binLoader.has(path);
  }
}

export { AREA_WIDTH, AREA_HEIGHT };
